import type { HTMLAttributes } from "react";

import { Badge, type BadgeProps } from "./Badge";
import { Tooltip } from "./Tooltip";

export interface BadgeGroupItem {
  label: string;
  title?: string;
  tone?: BadgeProps["tone"];
}

export interface BadgeGroupProps extends Omit<
  HTMLAttributes<HTMLSpanElement>,
  "children"
> {
  items: BadgeGroupItem[];
  /** Number of leading badges kept visible before the +N disclosure. */
  visibleCount?: number;
}

/**
 * Width-bounded badge run for table cells. Overflow is summarized as +N and
 * remains available in a hover/focus Tooltip instead of widening the column.
 */
export function BadgeGroup({
  className,
  items,
  visibleCount = 1,
  ...props
}: BadgeGroupProps) {
  const visible = items.slice(0, visibleCount);
  const hidden = items.slice(visibleCount);
  const classes = ["marker-badge-group"];
  if (className) {
    classes.push(className);
  }

  return (
    <span className={classes.join(" ")} {...props}>
      {visible.map((item) => (
        <Badge key={item.label} title={item.title} tone={item.tone}>
          {item.label}
        </Badge>
      ))}
      {hidden.length > 0 ? (
        <Tooltip content={hidden.map((item) => item.label).join(" · ")}>
          <Badge aria-label={`${String(hidden.length)} more`}>
            +{hidden.length}
          </Badge>
        </Tooltip>
      ) : null}
    </span>
  );
}
